import Cookies from 'js-cookie'

export const TOKEN_TYPE = {
  ACCESS_TOKEN: 'sso_access_token',
  REFRESH_TOKEN: 'sso_refresh_token',
  EXPIRES_AT: 'sso_token_expires_at',
} as const

export type TokenType = typeof TOKEN_TYPE[keyof typeof TOKEN_TYPE]

export const setTokenToCookies = (
  tokenType: TokenType,
  token: string,
  expiresInSeconds?: number,
  domain?: string
): void => {
  const options: Cookies.CookieAttributes = {
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
  }

  if (domain) {
    options.domain = domain
  }

  if (expiresInSeconds) {
    options.expires = new Date(Date.now() + expiresInSeconds * 1000)
  } else {
    const defaultDays = tokenType === TOKEN_TYPE.ACCESS_TOKEN ? 1 / 24 : 30
    options.expires = defaultDays
  }

  Cookies.set(tokenType, token, options)
}

export const getTokenFromCookies = (tokenType: TokenType): string | null => {
  return Cookies.get(tokenType) || null
}

export const removeTokenFromCookies = (tokenType: TokenType, domain?: string): void => {
  const options: Cookies.CookieAttributes = { path: '/' }
  if (domain) options.domain = domain
  Cookies.remove(tokenType, options)
}

export const clearAllTokens = (domain?: string): void => {
  removeTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN, domain)
  removeTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN, domain)
  removeTokenFromCookies(TOKEN_TYPE.EXPIRES_AT, domain)
}

export const isAuthenticated = (): boolean => {
  return !!getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN)
}

export const getAuthTokens = () => {
  return {
    accessToken: getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN),
    refreshToken: getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN),
    expiresAt: getTokenFromCookies(TOKEN_TYPE.EXPIRES_AT),
  }
}

export const isTokenExpiring = (): boolean => {
  const expiresAt = getTokenFromCookies(TOKEN_TYPE.EXPIRES_AT)
  if (!expiresAt) return true

  const expireTime = new Date(expiresAt)
  const now = new Date()
  const bufferTime = new Date(now.getTime() + 30 * 1000)

  return bufferTime >= expireTime
}

export const setTokenExpiry = (expiresAt: string, domain?: string): void => {
  setTokenToCookies(TOKEN_TYPE.EXPIRES_AT, expiresAt, undefined, domain)
}
